import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ProviderField, useProviderOptions } from "./ProviderField";

// Format a mailing address for display / placeholder (mirrors DemographicsTab).
function formatMailing(s) {
  if (!s) return "";
  const parts = [];
  if (s.mailing_street) parts.push(s.mailing_street);
  const cityLine = [s.mailing_city, s.mailing_state].filter(Boolean).join(", ");
  const cityZip = [cityLine, s.mailing_zip].filter(Boolean).join(" ");
  if (cityZip) parts.push(cityZip);
  if (parts.length) return parts.join("\n");
  return s.mailing_address || "";
}

export function TransportationTab({ student, editing, editData, setEditData, transportProviders = [], providerContacts = [] }) {
  const providerOptions = useProviderOptions(
    transportProviders,
    editData.transportation_provider,
    editData.second_transportation_provider,
  );

  // Look up a provider's saved contact number (case-insensitive).
  const phoneFor = (name) => {
    if (!name) return "";
    const hit = providerContacts.find((c) => (c.name || "").toLowerCase() === name.toLowerCase());
    return hit?.phone || "";
  };

  const primary = student.transportation_provider || "";
  const second = student.second_transportation_provider || "";
  const primaryPhone = phoneFor(primary);
  const secondPhone = phoneFor(second);

  return (
    <div className="space-y-6" data-testid="transportation-tab">
      {/* Transportation Address */}
      <Card>
        <CardHeader>
          <CardTitle>Transportation Address</CardTitle>
          <CardDescription>Where the student is picked up / dropped off. Defaults to the mailing address from Demographics when left blank.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Transportation Address</Label>
            {editing ? (
              <Input
                value={editData.transport_address || ""}
                onChange={(e) => setEditData((p) => ({ ...p, transport_address: e.target.value }))}
                placeholder={formatMailing(editData) || "Same as mailing address"}
                data-testid="transport-address-input"
              />
            ) : (
              <p className="text-sm py-2 whitespace-pre-wrap" data-testid="transport-address-value">
                {student.transport_address || formatMailing(student) || "-"}
                {!student.transport_address && formatMailing(student) && (
                  <span className="ml-2 text-xs text-muted-foreground">(from mailing address)</span>
                )}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Transportation Details */}
      <Card>
        <CardHeader>
          <CardTitle>Transportation Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {editing ? (
              <ProviderField
                label="Transportation Provider"
                value={editData.transportation_provider}
                options={providerOptions}
                onChange={(v) => setEditData((p) => ({ ...p, transportation_provider: v }))}
                testid="transportation-provider-select"
              />
            ) : (
              <div className="space-y-2">
                <Label>Transportation Provider</Label>
                <p className="text-sm py-2" data-testid="transportation-provider-value">{primary || "-"}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Provider Contact Number</Label>
              <p className="text-sm py-2" data-testid="transportation-provider-phone">
                {primaryPhone || <span className="text-muted-foreground">Not set — add it in Contacts → Transportation Providers</span>}
              </p>
            </div>

            {editing ? (
              <ProviderField
                label="Second Transportation Provider"
                value={editData.second_transportation_provider}
                options={providerOptions}
                onChange={(v) => setEditData((p) => ({ ...p, second_transportation_provider: v }))}
                testid="second-transportation-provider-select"
              />
            ) : (
              <div className="space-y-2">
                <Label>Second Transportation Provider</Label>
                <p className="text-sm py-2" data-testid="second-transportation-provider-value">{second || "-"}</p>
              </div>
            )}
            {(second || secondPhone) && (
              <div className="space-y-2">
                <Label>Second Provider Contact Number</Label>
                <p className="text-sm py-2" data-testid="second-transportation-provider-phone">
                  {secondPhone || <span className="text-muted-foreground">Not set</span>}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Pickup Time</Label>
              {editing ? (
                <Input
                  type="time"
                  value={editData.pickup_time || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, pickup_time: e.target.value }))}
                  data-testid="pickup-time-input"
                />
              ) : (
                <p className="text-sm py-2">{student.pickup_time || "-"}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
